"use client";

import { useState } from "react";

/**
 * The What's on newsletter sign-up (double opt-in, 1607). Posts to
 * /api/localized/newsletter; the confirmation email does the rest. Without
 * scripting the same form posts to the newsletter page, which answers in
 * HTML.
 */
export function GuideNewsletterForm({
  lang,
  action,
  regions,
  copy,
  compact = false,
}: {
  lang: "en" | "ar";
  /** The newsletter page (the no-script fallback target). */
  action: string;
  regions: Array<{ slug: string; label: string }>;
  copy: { email: string; regions: string; allRegions: string; submit: string; submitting: string; pending: string; error: string; tooMany: string; privacy: string };
  compact?: boolean;
}) {
  const [state, setState] = useState<"idle" | "busy" | "pending" | "error" | "tooMany">("idle");

  return (
    <form
      action={action}
      method="post"
      className={`eg-newsletter${compact ? " eg-newsletter--compact" : ""}`}
      onSubmit={async (event) => {
        event.preventDefault();
        const form = event.currentTarget;
        const data = new FormData(form);
        setState("busy");
        try {
          const res = await fetch("/api/localized/newsletter", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ email: data.get("email"), lang, regions: data.getAll("regions") }),
          });
          if (res.status === 429) setState("tooMany");
          else if (res.ok) {
            setState("pending");
            form.reset();
          } else setState("error");
        } catch {
          setState("error");
        }
      }}
    >
      <input type="hidden" name="lang" value={lang} />
      <div className="eg-newsletter__row">
        <label className="eg-newsletter__field">
          <span className="eg-eyebrow">{copy.email}</span>
          <input type="email" name="email" required autoComplete="email" inputMode="email" className="eg-input" placeholder="name@example.com" dir="ltr" />
        </label>
        <button type="submit" className="eg-button eg-button--primary" disabled={state === "busy"}>
          {state === "busy" ? copy.submitting : copy.submit}
        </button>
      </div>
      {!compact && regions.length > 0 ? (
        <fieldset className="eg-newsletter__regions">
          <legend className="eg-eyebrow">{copy.regions}</legend>
          <span style={{ fontSize: 13, color: "var(--eg-muted)" }}>{copy.allRegions}</span>
          {regions.map((r) => (
            <label key={r.slug} className="eg-check" style={{ fontSize: 14 }}>
              <input type="checkbox" name="regions" value={r.slug} />
              {r.label}
            </label>
          ))}
        </fieldset>
      ) : null}
      <p role="status" aria-live="polite" style={{ margin: 0, fontSize: 14, color: state === "error" || state === "tooMany" ? "var(--eg-red)" : "var(--eg-muted)" }}>
        {state === "pending" ? copy.pending : state === "error" ? copy.error : state === "tooMany" ? copy.tooMany : copy.privacy}
      </p>
    </form>
  );
}
